/// <amd-module name="typeahead/core/TypeaheadUtils"/>

/// <amd-dependency path="typeahead/core/loc" name="loc"/>
/// <amd-dependency path="typeahead/core/context" name="coreContext"/>
/// <amd-dependency name="uitk" path="uitk" />

/// <reference path="../../typings/thirdparty/modernizr.d.ts" />
/// <reference path="../../typings/typeahead/loc.d.ts" />
/// <reference path="../../typings/typeahead/window.d.ts" />
/// <reference path="../../typings/typeahead/uitk.d.ts" />

import {ITrackingData} from "typeahead/core/logging/OmnitureLogger";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {IEssSuggestionResult} from "typeahead/core/Suggestion";

export enum FlightType {
    OneWay, RoundTrip, MultiDestination, Unknown,
}

// This enum has sample LOB values to be logged into omniture reflecting the LOB grouping in any wizard
export enum Lob {
    Flight, Hotel, Package, Car, Cruise, Activity, Rail, VacationRental, OpenSearch, Unknown,
}

export enum PackageType {
    FlightHotel, FlightHotelCar, FlightCar, HotelCar, Unknown,
}

export enum RailType {
    OneWay, RoundTrip, Unknown,
}

export enum SiteParam {
    eapid, tpid,
}

export const MercuryWizardLandingPagesPackageTypes: string[] = [
    "packageFlightHotel", "flightHotel",
    "packageFlightHotelCar", "flightHotelCar", "carHotelFlight",
    "packageFlightCar", "flightCar", "carFlight",
    "packageHotelCar", "hotelCar", "carHotel",
];

export class TypeaheadUtils {
    public static readonly ZERO_RESULTS_CAUSED_BY_EXCEPTION: string = "ZERO_RESULTS_CAUSED_BY_EXCEPTION";

    public static responsiveTAClasses = {
        autoCompleteClass : "autocomplete",
        centerClass : "pin-center",
        dropdownClass : "autocomplete-dropdown",
        hlClass : "highlight",
        leftClass : "pin-left",
        openClass : "autocomplete-open",
        rightClass : "pin-right",
    };

    public static bitMaskFlags = {
        ADDRESS : 32,
        AIRPORT : 1,
        CITY : 2,
        HOTEL : 128,
        METROCODE : 64,
        METROSTATION : 1024,
        MULTICITY : 4,
        MULTIREGION : 256,
        NEIGHBORHOOD : 8,
        POI : 16,
        TRAINSTATION : 512,
    };

    public static defaultMask: number = (
        /* tslint:disable-next-line: no-bitwise */
        TypeaheadUtils.bitMaskFlags.AIRPORT |
        TypeaheadUtils.bitMaskFlags.CITY |
        TypeaheadUtils.bitMaskFlags.MULTICITY |
        TypeaheadUtils.bitMaskFlags.NEIGHBORHOOD |
        TypeaheadUtils.bitMaskFlags.POI
    );

    public static LOB_ATTRIBUTE = {
        ACTIVITIES: "ACTIVITIES",
        CARS: "CARS",
        FLIGHTS : "FLIGHTS",
        HOTELS : "HOTELS",
        PACKAGES : "PACKAGES",
        RAILS: "RAILS",
        TEST: "",
        VACATIONRENTALS: "VACATIONRENTALS",
    };

    public static CATEGORIES = {
        ADDRESS: {
            icon: "location",
            id: 4,
            name: loc.taCategoryNameAddress,
        },
        AIRPORT: {
            icon: "flights",
            id: 2,
            name: loc.taCategoryNameAirports,
        },
        ATTRACTION: {
            icon: "location",
            id: 1,
            name: loc.taCategoryNameAttractions,
        },
        CITY: {
            icon: "location",
            id: 0,
            name: loc.taCategoryNameRegionCity,
        },
        DEALS: {
            icon: "location",
            id: 13,
            name: loc.taCategoryNameDeals,
        },
        GEOLOCATION: {
            icon: "geolocation",
            id: 16,
            name: undefined,
        },
        HOTEL: {
            icon: "hotels",
            id: 3,
            name: loc.taCategoryNameHotels,
        },
        LIST: {
            icon: "list",
            id: 18,
        },
        METROCODE: {
            icon: "location",
            id: 9,
            name: loc.taCategoryNameRegionCity,
        },
        METROSTATION: {
            icon: "train",
            id: 6,
            name: loc.taCategoryNameMetros,
        },
        MULTICITY: {
            icon: "location",
            id: 7,
            name: loc.taCategoryNameRegionCity,
        },
        NEIGHBORHOOD: {
            icon: "location",
            id: 8,
            name: loc.taCategoryNameRegionCity,
        },
        POI: {
            icon: "location",
            id: 11,
            name: loc.taCategoryNameAttractions,
        },
        RENTAL: {
            icon: "cars",
            id: 12,
            name: loc.taCategoryNameRentals,
        },
        TRAINSTATION: {
            icon: "train",
            id: 5,
            name: loc.taCategoryNameTrains,
        },
        USERHISTORY: {
            icon: "notime",
            id: 15,
            name: loc.taCategoryNameRecentSearch,
        },
    };

    public static getCookie(name): string {
        const value = "; " + document.cookie;
        const parts = value.split("; " + name + "=");
        if (parts.length === 2) {
            return parts.pop().split(";").shift();
        }
        return "";
    }

    public static getAbovMapFromCookie(abovCookieValue): {[key: number]: number} {
        const abovMap: {[key: number]: number} = {};
        const isNum = (val) => /^\d+$/.test(val);
        if (typeof abovCookieValue === "string") {
            const experiments = abovCookieValue.split(":");
            experiments.forEach((experiment): void => {
                const expTokens = experiment.split("|");
                if (expTokens.length === 3 && expTokens[0].length > 0 && expTokens[2].length > 0
                        && isNum(expTokens[0]) && isNum(expTokens[2])) {
                    abovMap[parseInt(expTokens[0], 10)] = parseInt(expTokens[2], 10);
                }
            });
        }
        return abovMap;
    }

    public static getGuid(): string {
        let guid = window.dctk && window.dctk.logging && window.dctk.logging.guid ?
                    window.dctk.logging.guid : undefined;
        if (guid === undefined) {
            guid = window.utag_data && window.utag_data.guid ? window.utag_data.guid : undefined;
        }
        if (guid === undefined) {
            try {
                require(["gc/pageModel"], (gcPageModel) => {
                    if (gcPageModel && gcPageModel.user) {
                        guid = gcPageModel.user.guid;
                    }
                });
            } catch (e) {
                // Ignore
            }
        }
        if (guid === undefined) {
            const mc1Cookie = this.getCookie("MC1");
            const pair = mc1Cookie && mc1Cookie.split("=");
            guid = $.isArray(pair) && pair.length === 2 ? pair[1] : "";
        }
        if (guid === undefined) {
            TypeaheadErrorLogger.log(
                new Error("ERROR: Unable to get 'guid'"), Severity.MustFix, ProblemOrigin.ClientOfUniversalTypeahead);
        } else {
            guid = guid.replace(/-/g, "");
        }
        return guid;
    }

    // Get value of tpid or eapid
    public static getSiteParamValue(siteParam: SiteParam): number {
        let paramValue: number;
        const siteParamCookieConfig = {
            eapid: {
                cookieName: "iEAPID",
            },
            tpid: {
                cookieName: "tpid",
            },
        };
        const param: string = SiteParam[siteParam];
        if (window.utag_data && window.utag_data.context && window.utag_data.context.site
            && window.utag_data.context.site[param] !== undefined) {
            paramValue = window.utag_data.context.site[param];
        } else {
            try {
                require(["gc/pageModel"], (gcPageModel) => {
                    if (gcPageModel && gcPageModel.site && gcPageModel.site[param] !== undefined) {
                        paramValue = gcPageModel.site[param];
                    }
                });
            } catch (e) {
                // Ignore
            }
        }
        if (paramValue === undefined) {
            // Sample tpid cookie value - v.1,3
            // Sample iEAPID cookie value - 0
            const cookie: string = this.getCookie(siteParamCookieConfig[param].cookieName);
            if (cookie !== undefined) {
                const cookieTokens: string[] = cookie.split(",");
                paramValue = parseInt(cookieTokens.length > 1 ? cookieTokens[1] : cookieTokens[0], 10);
            }
        }
        if (typeof paramValue !== "number" || isNaN(paramValue)) {
            TypeaheadErrorLogger.log(new Error("Unable to get " + param + " from utag_data/cookies/gcPageModel"),
                Severity.Excusable, ProblemOrigin.ClientOfUniversalTypeahead);
        }
        return paramValue;
    }

    public static getPointOfSale(): string {
        return window.location.hostname;
    }

    public static getLocale(): string {
        return coreContext.locale;
    }

    public static getSiteId(): string {
        // Below type coversion to string is a hotfix.
        // coreContext.siteid should be ideally set as string in the typeahead java layer
        return coreContext.siteid + "";
    }

    public static getPageId(): string {
        return TypeaheadUtils.defaultPageIdGetter();
    }

    public static getDeviceType(): string {
        if (!TypeaheadUtils.deviceType) {
            TypeaheadUtils.deviceType = window.utag_data && window.utag_data.device_type ?
                window.utag_data.device_type : TypeaheadUtils.evaluateDeviceType();
        }
        return TypeaheadUtils.deviceType;
    }

    public static getBrowser(): string {
        let browser = (window.utag_data && window.utag_data.context && window.utag_data.context.clientInfo &&
            window.utag_data.context.clientInfo.deviceCharacteristics) ?
            window.utag_data.context.clientInfo.deviceCharacteristics.mobileBrowser : undefined;

        if (!browser) {
            browser = TypeaheadUtils.evaluateBrowserName();
        }
        return browser;
    }

    // Gets the client ID from the input element / meta DOM element / url PATH
    public static getClientId(): string {
        let clientId: string = $("input[data-provide=utypeahead]").data("clientId");
        if (!clientId) {
            clientId = $("meta[name=essClientId]").attr("content")
                || TypeaheadUtils.clientIdByPageId[TypeaheadUtils.defaultPageIdGetter()]
                || TypeaheadUtils.clientIdByPath[window.location.pathname];
            clientId = (["Hotels.Search", "Flights.Search"].indexOf(clientId) > -1
                && window.location.search.indexOf("packageType=") > -1) ? "Packages.Search" : clientId;
        }
        if (!clientId) {
            TypeaheadErrorLogger.log(new Error("Could not determine client id"), Severity.Excusable,
                ProblemOrigin.UniversalTypeahead);
            clientId = TypeaheadUtils.TEST_CLIENT_ID;
        }
        return clientId ? clientId : "Unknown";
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setClientId(clientId: string) {
        TypeaheadUtils.TEST_CLIENT_ID = clientId;
    }

    public static isStorefrontClient(): boolean {
        const storefrontClientIds: string[] = [
            "Homepage",
            "AWSHomepage",
            "LXLandingPage",
            "CarsLandingPage",
            "FlightsLandingPage",
            "HotelsLandingPage",
            "PackageLandingPage",
            "RailsLandingPage"];
        return storefrontClientIds.indexOf(TypeaheadUtils.getClientId()) > -1;
    }

    public static getLob(): Lob {
        if ("OpenSearchPage" === TypeaheadUtils.getClientId()) {
            return Lob.OpenSearch;
        }
        // Search if wizard page has LOB
        let lob: string = $("div.wizard-tabs").find("button.on").data("lob");
        // If page does not have LOB search if uTA input has data-lob
        lob = lob ? lob : $("input[data-provide=utypeahead]:visible").data("lob");
        // Check if the TEST LOB attribute is set
        lob = lob ? lob : TypeaheadUtils.LOB_ATTRIBUTE.TEST;

        if (typeof lob === "string") {
            lob = lob.toLowerCase();
            if (lob.indexOf("pack") > -1 ||
                MercuryWizardLandingPagesPackageTypes.map((type) => type.toLowerCase()).indexOf(lob) > -1) {
                return Lob.Package;
            } else if (lob.indexOf("flight") > -1) {
                return Lob.Flight;
            } else if (lob.indexOf("hotel") > -1) {
                return Lob.Hotel;
            } else if (lob.indexOf("vacationrental") > -1) {
                return Lob.VacationRental;
            } else if (lob.indexOf("car") > -1) {
                return Lob.Car;
            } else if (lob.indexOf("cru") > -1) {
                return Lob.Cruise;
            } else if (lob.indexOf("lx") > -1 || lob.indexOf("activit") > -1) {
                return Lob.Activity;
            } else if (lob.indexOf("train") > -1 || lob.indexOf("rail") > -1) {
                return Lob.Rail;
            } else if (lob.indexOf("open") > -1) {
                return Lob.OpenSearch;
            }
        }
        return Lob.Unknown;
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setLob(lob: string) {
        TypeaheadUtils.LOB_ATTRIBUTE.TEST = lob;
    }

    public static getFlightType(lob: Lob): FlightType {
        if (lob === Lob.Flight) {
            let flightTypeElementId =
                $("input[name=route-type], input[name=flight-type]").filter(":checked:visible").attr("id");
            flightTypeElementId = flightTypeElementId ? flightTypeElementId : TypeaheadUtils.TEST_FLIGHT_TYPE_ID;
            if (flightTypeElementId) {
                flightTypeElementId = flightTypeElementId.toLowerCase();
                if (flightTypeElementId.indexOf("one") > -1) {
                    return FlightType.OneWay;
                } else if (flightTypeElementId.indexOf("round") > -1) {
                    return FlightType.RoundTrip;
                } else if (flightTypeElementId.indexOf("multi") > -1) {
                    return FlightType.MultiDestination;
                } else {
                    TypeaheadErrorLogger.log(new Error("Could not determine flight type with dom element id - " +
                            flightTypeElementId),
                        Severity.Excusable,  ProblemOrigin.ClientOfUniversalTypeahead);
                }
            }
        }
        return FlightType.Unknown;
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setFlightElementId(id: string) {
        TypeaheadUtils.TEST_FLIGHT_TYPE_ID = id;
    }

    public static getRailType(lob: Lob): RailType {
        if (lob === Lob.Rail) {
            let railTypeElementId =
                $("input[name=journeyType]").filter(":checked").attr("id");
            railTypeElementId = railTypeElementId ? railTypeElementId : TypeaheadUtils.TEST_RAIL_TYPE_ID;
            if (railTypeElementId) {
                railTypeElementId = railTypeElementId.toLowerCase();
                if (railTypeElementId.indexOf("one") > -1) {
                    return RailType.OneWay;
                } else if (railTypeElementId.indexOf("round") > -1) {
                    return RailType.RoundTrip;
                } else {
                    TypeaheadErrorLogger.log(new Error("Could not determine rail journey type with dom element id - " +
                            railTypeElementId),
                        Severity.MustFix,  ProblemOrigin.ClientOfUniversalTypeahead);
                }
            }
            TypeaheadErrorLogger.log(new Error("Unable to find journeyType dom element to determine rail journey type"),
                Severity.MustFix,  ProblemOrigin.ClientOfUniversalTypeahead);
        }
        return RailType.Unknown;
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setRailTypeId(id: string) {
        TypeaheadUtils.TEST_RAIL_TYPE_ID = id;
    }

    public static getPackageType(searchBoxElement?: JQuery): PackageType {
        const specifiedPackageType: string =
            // Specified as data-package-type in the search box element
            (searchBoxElement ? searchBoxElement.data("package-type") : undefined)
            // Homepage:
            || $("input[name=package-lob], input[name=package-type]").filter(":visible:checked").attr("value")
            // Launch pages:
            || $("div.wizard-tabs").find("button.on").data("lob")
            // For unit testing only:
            || TypeaheadUtils.TEST_PACKAGE_TYPE_VALUE;
        if (specifiedPackageType) {
            const mappedPackageType: PackageType = this.packageTypes[specifiedPackageType.toLowerCase()];
            if (mappedPackageType) {
                return mappedPackageType;
            }
        }
        // Setting default package type as FH for POS that do not have package types like jp, sg & hk
        return (TypeaheadUtils.getLob() === Lob.Package) ? PackageType.FlightHotel : PackageType.Unknown;
    }

    public static getShortPackageType(searchBoxElement?: JQuery): string {
        const packageType: PackageType = TypeaheadUtils.getPackageType(searchBoxElement);
        switch (packageType) {
            case PackageType.FlightCar: return "FC";
            case PackageType.FlightHotel: return "FH";
            case PackageType.FlightHotelCar: return "FHC";
            case PackageType.HotelCar: return "HC";
            default:
                return undefined;
        }
    }

    // NOTE: Utility function to set LOB for testing purpose only
    // To be called from *.spec.ts ONLY
    public static setPackageTypeValue(value: string) {
        TypeaheadUtils.TEST_PACKAGE_TYPE_VALUE = value;
    }

    // UIS Prime Documentation: https://confluence.expedia.biz/display/UISPrime/UIS+Prime+Site+Action%3A+ESS+TypeAhead
    public static submitToOmniture(eventName: string, trackingData: ITrackingData, activeESSTests: string): void {
        const lob: Lob = TypeaheadUtils.getLob();
        const flightType: FlightType = TypeaheadUtils.getFlightType(lob);
        if (lob === Lob.Flight && flightType === FlightType.MultiDestination) {
            // TO-DO: Correctly handle multi-flight logging
            return;
        }
        let platformAnalyticsAvailable = false;
        require(["PlatformAnalytics"], (platformAnalytics) => {
            platformAnalyticsAvailable = true;
            const trackingEvents = [];
            trackingEvents.push(this.getReferrerEvent(trackingData.eVar28, eventName, "click"));
            trackingEvents.push(...this.extractTrackingDataEvents(trackingData));
            trackingEvents.push(...this.extractSelectedSuggestionEvents(trackingData));

            const instance = platformAnalytics.getInstance() || platformAnalytics.newInstance();
            instance.trackEvent(trackingEvents);
        });
        setTimeout(() => {
            if (!platformAnalyticsAvailable) {
                require(["dctk/dctk"], (dctk) => {
                    if (activeESSTests) {
                        dctk.addExperiments(activeESSTests);
                    }
                    dctk.trackEvent("o", eventName, trackingData, trackingData.eventString);
                });
            }
        }, 0);
    }

    public static isWithinMobileBreakpoint(): boolean {
        return (uitk && uitk.mediaquery &&
            uitk.mediaquery.getMediaQueries() &&
            uitk.mediaquery.getMediaQueries().mobile &&
            uitk.mediaquery.getMediaQueries().mobile.matches);
    }

    public static isTouchDevice(): boolean {
        return Modernizr && Modernizr.touch;
    }

    public static getExpUserId(): string {
        let expuserid: string = $("#scratchpad-badge-expuserid").attr("data-id");
        if (!expuserid) {
            require(["gc/pageModel"], (gcPageModel) => {
                expuserid = gcPageModel.user ? gcPageModel.user.expUserId : undefined;
            });
        }
        return expuserid || "-1";
    }

    public static iconOverride(category: any, icon: string): any {
        // creates a copy of the category in order to not override the icon
        // in the original category entry, and returns the copy with icon replaced
        const copy = { ...category};
        copy.icon = icon;
        return copy;
    }

    public static getOverrideIcon(type: string, subtype?: string): any {
        if (typeof subtype === "string") {
            type = type + "|" + subtype;
        }
        return TypeaheadUtils.categoryIconOverrides[type];
    }

    public static isNearBySuggestion(suggestion: IEssSuggestionResult): boolean {
        const NEARBY_RELATION: string = "nearby";
        const relations: string[] = suggestion.hierarchyInfo ? (suggestion.hierarchyInfo.relation || []) : [];
        return relations.indexOf(NEARBY_RELATION) > -1;
    }

    public static isChildSuggestion(suggestion: IEssSuggestionResult, inputTriggerType?: string): boolean {
        const HIERARCHY_RELATIONS: string[] = ["child", "lxcategory", "nearby"];
        const relation: string[] = suggestion.hierarchyInfo ? (suggestion.hierarchyInfo.relation || []) : [];
        const isAlternateRegionChild: boolean = ("alt" === relation[0] && "focus" === inputTriggerType);
        return HIERARCHY_RELATIONS.indexOf(relation[0]) > -1 || isAlternateRegionChild;
    }

    // Code used from - https://stackoverflow.com/questions/11582512/how-to-get-url-parameters-with-javascript
    public static getURLParameter(name: string, url: string = location.search): string {
        return decodeURIComponent((new RegExp("[?|&]" + name + "=" + "([^&;]+?)(&|#|;|$)").exec(url)
            || [null, ""])[1].replace(/\+/g, "%20")) || null;
    }

    public static getUtaBundleUrl(): string {
        return $("script[src*='universal_typeahead_bundle']").attr("src");
    }

    public static getUserLocation(): string {
        let userLocation: string;
        try {
            require(["storefrontPageModel"], (pageModel) => {
                if (pageModel.userInteraction
                    && pageModel.userInteraction.context
                    && pageModel.userInteraction.context.clientInfo
                    && pageModel.userInteraction.context.clientInfo.coords) {
                    const coords: any = pageModel.userInteraction.context.clientInfo.coords;
                    if (coords.latitude && coords.longitude) {
                        userLocation = coords.latitude + "|" + coords.longitude;
                        return;
                    }
                }
            });
        } catch (e) { /* Ignore */ }
        return userLocation;
    }

    public static isStatusCodeInRange(statusCodeString: string, begin: number, end: number): boolean {
        const statusCode: number = Number(statusCodeString);
        if (isNaN(statusCode)) {
            return false;
        }

        return begin <= statusCode && statusCode <= end;
    }

    public static getEssAbTestVariant(expId: string, essAbTests: string): string {
        if (essAbTests) {
            const arrayEssAbTests: string[] = essAbTests.split("|");
            for (let i: number = 0; i < arrayEssAbTests.length; i++) {
               if (arrayEssAbTests[i].indexOf(expId) > -1) {
                   const expTokens: string[] = arrayEssAbTests[i].split(".");
                   if (expTokens.length > 1) {
                       return expTokens[1];
                   }
               }
           }
        }
        return undefined;
    }

    public static isRtl(): boolean {
        return uitk.isRtl !== undefined ? uitk.isRtl : ($("html").attr("dir") === "rtl");
    }

    public static isTrunkEnvironment(): boolean {
        return location.host.indexOf(".trunk.sb.karmalab.net") > -1
            || ["test", "dev"].indexOf(coreContext.environment) > -1;
    }

    public static getServiceUrl(): string {
        return TypeaheadUtils.isTrunkEnvironment() ? coreContext.serviceUrl : ""; // same as current host
    }

    private static categoryIconOverrides = {
        "ADDRESS|RENTALLOCATION" : TypeaheadUtils.CATEGORIES.RENTAL.icon,
        "NEIGHBORHOOD|AIRNBH": "flights",
    };

    private static TEST_CLIENT_ID = "Unknown";
    private static TEST_FLIGHT_TYPE_ID = "Unknown";
    private static TEST_PACKAGE_TYPE_VALUE = "Unknown";
    private static TEST_RAIL_TYPE_ID = "Unknown";

    private static clientIdByPageId = {
        "Homepage": "Homepage",
        "activity": "LXLandingPage",
        "aws_Homepage": "Homepage",
        "car": "CarsLandingPage",
        "cruise": "Unknown",
        "flight": "FlightsLandingPage",
        "homepage": "Homepage",
        "hotel": "HotelsLandingPage",
        "package": "PackageLandingPage",
        "page.Account.MyAccount": "UserAccountPage",
        "page.Activities": "LXLandingPage",
        "page.Cars": "CarsLandingPage",
        "page.Cars.Infosite.Information": "Cars.Search",
        "page.Checkout.Payment": "Unknown",
        "page.Domestic": "HotelsLandingPage", // https://www.expedia.co.jp/domestic?langid=1041
        "page.Flight-Search-Oneway": "Flights.Search",
        "page.Flight-Search-Roundtrip.Out": "Flights.Search",
        "page.Flight-Search.MDest": "Flights.Search",
        "page.Flights": "FlightsLandingPage",
        "page.Hotel-Search": "Hotels.Search",
        "page.Hotels": "HotelsLandingPage",
        "page.Hotels.Search": "Hotels.Search",
        "page.Package.FC.Flight-Search-Roundtrip.Out": "Packages.Search",
        "page.Package.FH.Hotel-Search": "Packages.Search",
        "page.Package.FHC.Hotel-Search": "Packages.Search",
        "page.Package.HC.Hotel-Search": "Packages.Search",
        "page.Packages": "PackageLandingPage",
        "page.Rails": "RailsLandingPage",
        "page.car.search.list.aws": "Cars.Search",
        "page.car.search.list.dropOff.aws": "Cars.Search",
        "page.open-search": "OpenSearchPage",
        "threepp": "Unknown",
    };

    private static clientIdByPath = {
        "/": "Homepage",
        "/Activities" : "LXLandingPage",
        "/Activities/" : "LXLandingPage",
        "/Cars" : "CarsLandingPage",
        "/Cars/" : "CarsLandingPage",
        "/Cruises": "Unknown",
        "/Cruises/": "Unknown",
        "/Flights": "FlightsLandingPage",
        "/Flights-Search": "Flights.Search",
        "/Flights-Search/": "Flights.Search",
        "/Flights/": "FlightsLandingPage",
        "/Holidays": "PackageLandingPage",
        "/Holidays/": "PackageLandingPage",
        "/Hotel-Search": "Hotels.Search",
        "/Hotel-Search/": "Hotels.Search",
        "/Hotels": "HotelsLandingPage",
        "/Hotels/": "HotelsLandingPage",
        "/MultiItemCheckout": "Unknown",
        "/MultiItemCheckout/": "Unknown",
        "/Vacation-Packages": "PackageLandingPage",
        "/Vacation-Packages/": "PackageLandingPage",
        "/carsearch": "Cars.Search",
        "/carsearch/": "Cars.Search",
        "/carsearch/dropoff": "Cars.Search",
        "/search-results": "OpenSearchPage",
        "/trains": "RailsLandingPage",
    };

    private static deviceType;

    private static readonly packageTypes: {[key: string]: PackageType} = Object.freeze({
        "carflight": PackageType.FlightCar,
        "carhotel": PackageType.HotelCar,
        "carhotelflight": PackageType.FlightHotelCar,
        "fc": PackageType.FlightCar,
        "fh": PackageType.FlightHotel,
        "fhc": PackageType.FlightHotelCar,
        "flight-car": PackageType.FlightCar,
        "flight-hotel": PackageType.FlightHotel,
        "flight-hotel-car": PackageType.FlightHotelCar,
        "flightcar": PackageType.FlightCar,
        "flighthotel": PackageType.FlightHotel,
        "flighthotelcar": PackageType.FlightHotelCar,
        "hc": PackageType.HotelCar,
        "hotel-car": PackageType.HotelCar,
        "hotelcar": PackageType.HotelCar,
        "packageflightcar": PackageType.FlightCar,
        "packageflighthotel": PackageType.FlightHotel,
        "packageflighthotelcar": PackageType.FlightHotelCar,
        "packagehotelcar": PackageType.HotelCar,
    });

    /* Device type evaluation code referred from -
     *   https://ewegithub.sb.karmalab.net/EWE/tealium/blob/master/src/main/extension/device-characteristics.js
     */
    private static evaluateDeviceType(): string {
        if (/iPad|Transformer|ME173X|KOOF|Nook|IMM76D|Nexus 10|Nexus 7|Tablet/i.test(navigator.userAgent) ||
            /HP Slate|PG09410|PG41200|Mediapad|Kindle|Tab|Xoom|TegraNote|SCH-I800/i.test(navigator.userAgent)) {
            return "Tablet";
        } else if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            return "Mobile";
        } else {
            return "Desktop";
        }
    }

    private static evaluateBrowserName(): string {
        const ua = navigator.userAgent;
        let tem;
        let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return "IE";
        }
        if (M[1] === "Chrome") {
            tem = ua.match(/\bOPR\/(\d+)/);
            if (tem != null) {
                return "Opera";
            }
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];
        tem = ua.match(/version\/(\d+)/i);
        if (tem != null) {
            M.splice(1, 1, tem[1]);
        }
        return M[0];
    }

    private static defaultPageIdGetter(): string {
        let pageId: string = window.xPageId
            || window.pageName
            || (window.dctk && window.dctk.logging && window.dctk.logging.pageName)
            || $("#pageId").val();

        if (!pageId) { // Get page id on Open Search Results Page
            require(["pageData"], (pageData) => {
                pageId = pageData.pageId;
            });
        }
        return pageId ? pageId : "";
    }

    private static getReferrerEvent(aReferrerId: string, aLinkName: string, anEventType: string): {} {
        return {
            messageContent: {
                eventType: anEventType,
                linkName: aLinkName,
                referrerId: aReferrerId,
            },
            schemaName: "referrer",
        };
    }

    private static extractTrackingDataEvents(trackingData: ITrackingData) {
        const trackingEvents: any[] = [];
        const eventStringSplit: string[] = trackingData.eventString.split(",");
        for (const event of eventStringSplit) {
            if (event) {
                const eventSplit = event.split("=");
                switch (eventSplit[0]) {
                    case "event44":
                        const selectionDepth = parseInt(eventSplit[1], 10);
                        trackingEvents.push(
                            this.getStandardEvent("TypeaheadSelectionDepth", selectionDepth),
                        );
                        break;
                    case "event45":
                        trackingEvents.push(
                            this.getStandardEvent("TypeaheadSearch", 0),
                        );
                        break;
                    case "event46":
                        const charsTyped = parseInt(eventSplit[1], 10);
                        trackingEvents.push(
                            this.getStandardEvent("TypeaheadCharactersTyped", charsTyped),
                        );
                        break;
                }
            }
        }
        return trackingEvents;
    }

    private static getStandardEvent(aName: string, aValue: any) {
        return {
            messageContent: {
                name: aName,
                value: aValue,
            },
            schemaName: "events",
        };
    }

    private static extractSelectedSuggestionEvents(trackingData: ITrackingData) {
        const suggestionTrackingEvents: any[] = [];
        const originInfoSplit = trackingData.prop73.split("|");
        const destInfoSplit = trackingData.eVar48.split("|");
        const otherInfoSplit = trackingData.prop74.split("|");
        this.addSelectedSuggestionEvent(originInfoSplit, destInfoSplit, otherInfoSplit, suggestionTrackingEvents);
        return suggestionTrackingEvents;
    }

    private static addSelectedSuggestionEvent(originInfoSplit, destInfoSplit, otherInfoSplit,
                                              suggestionTrackingEvents: any[]) {
        const selectedSuggestionsList = [];
        if (originInfoSplit.length >= 5 && otherInfoSplit.length >= 2) {
            const sourceInfo = originInfoSplit[0].split(":");
            if (sourceInfo.length >= 2) {
                selectedSuggestionsList.push({
                    essRequestId: otherInfoSplit[1],
                    lastSearchName: originInfoSplit[4],
                    relation: originInfoSplit[2].replace("R#", ""),
                    searchField: "origin",
                    sourceId: parseInt(sourceInfo[1], 10) || -1,
                    sourceName: sourceInfo[0] !== "-" ? sourceInfo[0] : "UNKNOWN",
                    suggestionType: originInfoSplit[1] !== "-" ? originInfoSplit[1] : "UNKNOWN",
                    userTypedQuery: otherInfoSplit[0].split("\"").join(""),
                });
            }
        }
        if (destInfoSplit.length >= 5 && otherInfoSplit.length >= 2) {
            const sourceInfo = destInfoSplit[0].split(":");
            if (sourceInfo.length >= 2) {
                selectedSuggestionsList.push({
                    essRequestId: otherInfoSplit[1],
                    lastSearchName: destInfoSplit[4],
                    relation: destInfoSplit[2].replace("R#", ""),
                    searchField: "dest",
                    sourceId: parseInt(sourceInfo[1], 10) || -1,
                    sourceName: sourceInfo[0] !== "-" ? sourceInfo[0] : "UNKNOWN",
                    suggestionType: destInfoSplit[1] !== "-" ? destInfoSplit[1] : "UNKNOWN",
                    userTypedQuery: otherInfoSplit[0].split("\"").join(""),
                });
            }
        }
        if (selectedSuggestionsList.length) {
            suggestionTrackingEvents.push({
                messageContent: {
                    selectedSuggestions: selectedSuggestionsList,
                },
                schemaName: "allSelectedSuggestions",
            });
        }
    }
}
